import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
      isExpired: (widgetId: string) => boolean;
    };
  }
}

type TurnstileRenderOptions = {
  sitekey: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  action?: string;
  cData?: string;
  'retry-interval'?: number;
  callback?: (token: string) => void;
  'error-callback'?: (err: string) => void;
  'expired-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'after-interactive-callback'?: () => void;
};

export type TurnstileProps = {
  sitekey: string;
  onVerify: (token: string) => void;
  onError?: (err: string) => void;
  onExpire?: () => void;
  appearance?: 'always' | 'execute' | 'interaction-only';
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
};

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile script'));
      });
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

export function Turnstile({
  sitekey,
  onVerify,
  onError,
  onExpire,
  appearance = 'always',
  theme = 'auto',
  size = 'normal',
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  // Keep callbacks in refs so the widget doesn't need to remount on every render
  const callbackRefs = useRef({ onVerify, onError, onExpire });
  useEffect(() => {
    callbackRefs.current = { onVerify, onError, onExpire };
  });

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !containerRef.current || !window.turnstile) return;

    const id = window.turnstile.render(containerRef.current, {
      sitekey,
      appearance,
      theme,
      size,
      callback: (token) => callbackRefs.current.onVerify(token),
      'error-callback': (err) => callbackRefs.current.onError?.(err),
      'expired-callback': () => callbackRefs.current.onExpire?.(),
    });

    widgetIdRef.current = id;

    return () => {
      if (widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [ready, sitekey, appearance, theme, size]);

  return <div ref={containerRef} />;
}
